import { apiFetch } from "@/lib/api"
import { API_BASE } from "@/lib/config"

/**
 * Client of /v1/hris — the account's HR-system connections
 * (docs/integrations/hris/README.md). Runs are executed on the queue: start
 * returns a run with status "running", poll fetchRun until it finishes.
 */

/** Catalog ids served by our own HRIS backend instead of the generic catalog flow. */
export const HRIS_PROVIDER_IDS = ["lucca", "hibob"] as const

export function isHrisProvider(id: string): boolean {
  return (HRIS_PROVIDER_IDS as readonly string[]).includes(id)
}

export type HrisConnectionStatus = "draft" | "active" | "paused" | "error"

export type HrisSettings = {
  legalEntityMap: Record<string, number>
  defaultRole: number
  emailSource: "work" | "work_then_personal"
  createDepartments: boolean
  costCenterAxisId: string | null
  archiveGuardPercent: number
  archiveGuardMax: number
  leaveOfferDaysBefore: number
  /** Schedule E §3.1: receive date of birth, sex and nationality for tickets. */
  travellerIdentity: boolean
  expenseWriteBack: boolean
  expenseWriteBackSince: number | null
  expenseCategoryMap: Partial<Record<string, string>>
  purchaseWriteBack: boolean
  purchaseCategoryId: string | null
  purchaseCostCenterId: string | null
}

export type HrisConnection = {
  id: number
  provider: string
  providerName: string
  status: HrisConnectionStatus
  tenantUrl: string
  defaultCompany: number | null
  hasCredentials: boolean
  /** The HR system has an expense or purchase module Travel Code can write to. */
  writeBack: boolean
  /** Traveller identity fields this HR system can give: Lucca all three, HiBob nationality only. */
  identityFields: ("birthDate" | "gender" | "nationality")[]
  settings: HrisSettings
  lastSyncAt: string | null
  lastSyncStatus: HrisRunStatus | null
  lastError: string | null
  updates: {
    mode: "realtime" | "nightly"
    webhookStatus: "off" | "active" | "unavailable"
    webhookError: string | null
    /** Lucca only: an OAuth client for webhooks is stored. */
    oauthClient: boolean
    nightlyAtUtc: string
  }
}

export type HrisEmployeeState = "pending" | "synced" | "attention" | "skipped" | "archived"

export type HrisEmployeeFilter = HrisEmployeeState | "new" | "all"

export type HrisEmployeeRow = {
  externalId: string
  name: string
  email: string | null
  legalEntity: string | null
  department: string | null
  employed: boolean
  state: HrisEmployeeState
  action: string | null
  reason: string | null
  reasonLabel: string | null
  error: string | null
  userId: number | null
  decision: "include" | "skip" | null
  decisionCompany: number | null
  decisionEmail: string | null
  firstSeenAt: string
  syncedAt: string | null
}

export type HrisEmployeesPage = {
  items: HrisEmployeeRow[]
  meta: { page: number; perPage: number; total: number; pages: number }
  counts: Record<HrisEmployeeFilter, number> & { heldArchives: number }
  companies: { id: number; name: string }[]
}

export type LegalHistory = {
  acceptances: {
    versionLabel: string | null
    acceptedAt: string
    by: { name: string; email: string | null } | null
    title: string | null
    method: string
    superseded: boolean
  }[]
  consents: {
    grantedAt: string
    grantedBy: { name: string; email: string | null } | null
    withdrawnAt: string | null
    withdrawnBy: { name: string; email: string | null } | null
    screenVersion: string | null
  }[]
}

export type HrisRunStatus = "running" | "ok" | "blocked" | "failed"

export type HrisAction = "create" | "link" | "update" | "archive" | "restore" | "unchanged" | "skip" | "conflict"

export type HrisRunItem = {
  action: HrisAction
  externalId: string
  displayName: string
  userId: number | null
  email: string | null
  newLink: boolean
  changes: string[]
  reason: string | null
  reasonLabel: string | null
  outcome: { status: "done" | "failed" | "held"; error: string | null } | null
}

export type HrisRun = {
  id: number
  kind: "preview" | "sync"
  trigger: string
  status: HrisRunStatus
  error: string | null
  stats: { planned: Record<HrisAction, number>; done: number; failed: number; held: number } | null
  archiveBlocked: boolean
  archiveBlockReason: string | null
  startedAt: string
  finishedAt: string | null
  items?: HrisRunItem[]
}

/** Schedule E §8: travel offers before approved leave, switched on by the company's own permission. */
export type HrisLeaveOffers = {
  /** Schedule E and the HR-data permission are in force. */
  available: boolean
  enabled: boolean
  grantedAt: string | null
  grantedBy: { name: string; email: string | null } | null
  daysBefore: number
  daysOptions: number[]
  consent: {
    code: string
    screenVersion: string
    name: string
    summary: string
    affirmation: string
    withdrawal: string
  }
  preview: { subject: string; html: string }
}

export type HrisLegalEntities = {
  legalEntities: { id: string; name: string; archived: boolean; companyId: number | null }[]
  companies: { id: number; name: string }[]
}

/** A 422 from the API: field → messages. */
export class HrisApiError extends Error {
  fields: Record<string, string[]>
  status: number

  constructor(message: string, fields: Record<string, string[]> = {}, status = 0) {
    super(message)
    this.fields = fields
    this.status = status
  }
}

const BASE = `${API_BASE}/hris`

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(`${BASE}${path}`, {
    ...init,
    headers: { Accept: "application/json", "Content-Type": "application/json", ...(init?.headers ?? {}) },
  })
  const body = await res.json().catch(() => null)
  if (!res.ok) {
    const fields: Record<string, string[]> = body?.errors ?? {}
    const first = Object.values(fields)[0]?.[0]
    throw new HrisApiError(first ?? body?.message ?? `Request failed (${res.status})`, fields, res.status)
  }
  return body as T
}

/**
 * The last answer of each tab's read, so going back to a tab shows it at once
 * while a fresh read runs (the HR-system lists behind some of them take a
 * second or more on a cold server cache).
 */
const tabCache = new Map<string, unknown>()

export function cachedRead<T>(key: string): T | undefined {
  return tabCache.get(key) as T | undefined
}

function remembered<T>(key: string, read: Promise<T>): Promise<T> {
  return read.then((value) => {
    tabCache.set(key, value)
    return value
  })
}

/** Warms every tab's data in the background when the page opens. */
export function prefetchTabs(id: number): void {
  void Promise.allSettled([fetchLegalEntities(id), fetchLeaveOffers(id), fetchExpenses(id), fetchPurchases(id)])
}

export function fetchConnections(): Promise<{ connections: HrisConnection[] }> {
  return request("/connections")
}

export function createConnection(payload: { provider: string; tenantUrl: string; apiKey: string }): Promise<HrisConnection> {
  return request("/connections", { method: "POST", body: JSON.stringify(payload) })
}

/** HR systems connected by approving our app in them (OAuth), not with a typed key. */
export const OAUTH_PROVIDERS: readonly string[] = ["hibob"]

/** Where the HR system's admin approves the app; the HR system then sends them back with ?install=. */
export function startOAuth(provider: string): Promise<{ installUrl: string }> {
  return request(`/oauth/${provider}/start`, { method: "POST", body: "{}" })
}

/** Attaches the approved install to the signed-in account as a draft connection. */
export function claimOAuthInstall(provider: string, install: string): Promise<HrisConnection> {
  return request(`/oauth/${provider}/claim`, { method: "POST", body: JSON.stringify({ install }) })
}

export function updateConnection(
  id: number,
  payload: { settings?: Partial<HrisSettings>; apiKey?: string; defaultCompany?: number | null },
): Promise<HrisConnection> {
  return request(`/connections/${id}`, { method: "PUT", body: JSON.stringify(payload) })
}

export function deleteConnection(id: number): Promise<{ ok: boolean }> {
  return request(`/connections/${id}`, { method: "DELETE" })
}

export function fetchLegalEntities(id: number): Promise<HrisLegalEntities> {
  return remembered(`legal-entities:${id}`, request(`/connections/${id}/legal-entities`))
}

export function startPreview(id: number): Promise<HrisRun> {
  return request(`/connections/${id}/preview`, { method: "POST" })
}

export function startSync(id: number, confirmArchives = false): Promise<HrisRun> {
  return request(`/connections/${id}/sync`, { method: "POST", body: JSON.stringify({ confirmArchives }) })
}

export function activateConnection(id: number): Promise<HrisConnection> {
  return request(`/connections/${id}/activate`, { method: "POST" })
}

export function pauseConnection(id: number): Promise<HrisConnection> {
  return request(`/connections/${id}/pause`, { method: "POST" })
}

export function fetchRuns(id: number): Promise<{ runs: HrisRun[] }> {
  return request(`/connections/${id}/runs`)
}

export function fetchRun(id: number, runId: number): Promise<HrisRun> {
  return request(`/connections/${id}/runs/${runId}`)
}

export function fetchEmployees(
  id: number,
  params: { tab: HrisEmployeeFilter; q?: string; page?: number },
): Promise<HrisEmployeesPage> {
  const qs = new URLSearchParams({ tab: params.tab, q: params.q ?? "", page: String(params.page ?? 1) })
  return request(`/connections/${id}/employees?${qs.toString()}`)
}

export function decideEmployee(
  id: number,
  externalId: string,
  payload: { decision: "include" | "skip" | "reset"; companyId?: number | null; email?: string | null },
): Promise<{ employee: HrisEmployeeRow; run: HrisRun | null }> {
  return request(`/connections/${id}/employees/${encodeURIComponent(externalId)}/decision`, {
    method: "POST",
    body: JSON.stringify(payload),
  })
}

export type RealtimeOAuthClient = { oauthClientId: string; oauthClientSecret: string }

/** Lucca needs an OAuth client for webhooks: send it the first time, retry without. */
export function enableRealtime(id: number, oauthClient?: RealtimeOAuthClient): Promise<HrisConnection> {
  return request(`/connections/${id}/webhook`, { method: "POST", body: JSON.stringify(oauthClient ?? {}) })
}

export function disableRealtime(id: number): Promise<HrisConnection> {
  return request(`/connections/${id}/webhook`, { method: "DELETE" })
}

/** Who accepted Schedule E and who gave or withdrew the HR-data permission (signers only). */
export async function fetchLegalHistory(): Promise<LegalHistory | null> {
  const res = await apiFetch(`${API_BASE}/legal/history?documentCode=SCH_E&consentCode=HRIS_DATA_SYNC`, {
    headers: { Accept: "application/json" },
  })
  if (res.status === 403) return null
  if (!res.ok) throw new HrisApiError(`Request failed (${res.status})`)
  return (await res.json()) as LegalHistory
}

export async function withdrawHrisPermission(): Promise<void> {
  const res = await apiFetch(`${API_BASE}/legal/consents/HRIS_DATA_SYNC`, {
    method: "DELETE",
    headers: { Accept: "application/json" },
  })
  if (!res.ok) throw new HrisApiError(`Request failed (${res.status})`)
}

export function fetchLeaveOffers(id: number): Promise<HrisLeaveOffers> {
  return remembered(`leave-offers:${id}`, request(`/connections/${id}/leave-offers`))
}

/** Schedule E §4.2(b): the notice the customer gives its employees, company and HR system filled in. */
export function fetchEmployeeNotice(id: number, language: string): Promise<{ language: string; languages: string[]; text: string }> {
  return request(`/connections/${id}/employee-notice?language=${encodeURIComponent(language)}`)
}

export type HrisExpenseKind = "flight" | "hotel" | "train" | "car" | "taxi" | "meals" | "other"

/** Schedule E §2.2: approved expenses written to the HR system's expense module. */
export type HrisExpenses =
  | { supported: false }
  | {
      supported: true
      available: boolean
      enabled: boolean
      since: string | null
      kinds: HrisExpenseKind[]
      categories: { id: string; name: string }[]
      categoriesError: string | null
      map: Partial<Record<HrisExpenseKind, string>>
      suggested: Partial<Record<HrisExpenseKind, string>>
      counts: Partial<Record<"sent" | "failed" | "skipped", number>>
      recent: {
        expenseId: number
        status: "sent" | "failed" | "skipped"
        externalId: string | null
        receipt: boolean
        note: string | null
        amount: number | null
        currency: string | null
        merchant: string | null
        date: string | null
        updatedAt: string
      }[]
    }

export function fetchExpenses(id: number): Promise<HrisExpenses> {
  return remembered(`expenses:${id}`, request(`/connections/${id}/expenses`))
}

export function runExpenses(id: number): Promise<{ queued: boolean }> {
  return request(`/connections/${id}/expenses/run`, { method: "POST" })
}

/** Company-paid trips as committed purchases in the HR system's procurement module. */
export type HrisPurchases =
  | { supported: false }
  | {
      supported: true
      available: boolean
      enabled: boolean
      since: string | null
      categories: { id: string; name: string }[]
      categoriesError: string | null
      categoryId: string | null
      suggestedId: string | null
      costCenters: { id: string; name: string }[]
      costCenterId: string | null
      counts: Partial<Record<"sent" | "failed" | "skipped" | "closed", number>>
      recent: {
        orderId: number
        orderCode: string | null
        date: string | null
        status: "sent" | "failed" | "skipped" | "closed"
        externalId: string | null
        amount: number | null
        currency: string | null
        note: string | null
      }[]
    }

export function fetchPurchases(id: number): Promise<HrisPurchases> {
  return remembered(`purchases:${id}`, request(`/connections/${id}/purchases`))
}

export function runPurchases(id: number): Promise<{ queued: boolean }> {
  return request(`/connections/${id}/purchases/run`, { method: "POST" })
}

export type HrisAccessReason = "rollout" | "staff" | "role" | "plan"

/** Whether the caller may connect the account's HR system, and if not, why. */
export function fetchHrisAccess(): Promise<{ allowed: boolean; reason: HrisAccessReason | null }> {
  return request("/access")
}
